// frontend/src/components/IndependenceTrendSection.jsx
// 教育進捗ダッシュボード - 直近の自立者推移
//
// 「自立」の定義: skill_records.level が「独り立ち」に初めて到達したタイミング。
//   - evaluation_history (jsonb array of { level, evaluated_at, evaluated_by }) を解析して
//     level === '独り立ち' の中で最も古い evaluated_at を「自立日」とする
//   - 現在の level が '指導可' でも、過去に '独り立ち' を通っていれば history に残っている想定
//
// 可視化:
//   1. 術式別 自立者数推移 (月別積み上げ棒グラフ、各 segment = 術式)
//   2. 自立者一覧 (選択した術式 or フィルタ中の全術式、名前 / 自立日 ソート可)

import { useEffect, useMemo, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { Search } from "lucide-react";
import { supabase } from "../supabase";

const ROLE_LABEL = { scrub: "器械出し", circulating: "外回り" };
const ROLE_COLOR = { scrub: "#0891b2", circulating: "#7c3aed" };
const INDEPENDENT_LEVEL = "独り立ち";
const INDEPENDENT_PLUS_LEVELS = ["独り立ち", "指導可"];
const PAGE_SIZE = 1000;

function listMonths(from, to) {
  if (!from || !to) return [];
  const [fy, fm] = from.split("-").map(Number);
  const [ty, tm] = to.split("-").map(Number);
  if (!fy || !fm || !ty || !tm) return [];
  const months = [];
  let y = fy, m = fm;
  for (let i = 0; i < 240; i++) {
    months.push(`${y}-${String(m).padStart(2, "0")}`);
    if (y > ty || (y === ty && m >= tm)) break;
    m++;
    if (m > 12) { m = 1; y++; }
  }
  return months;
}

// skill_records をページネーションで全件取得。
// 現在 or 過去に 独り立ち 以上であった行だけ返したいが、evaluation_history の判定は
// 後段のクライアント側で行うため、ここでは level が '独り立ち' / '指導可' のものに絞る。
// (※ 一旦達成後に降格されたレアケースは漏れるが、まずは正道のケースを優先)
async function fetchIndependentCapableSkillRecords(organizationId) {
  const all = [];
  let page = 0;
  while (true) {
    const { data, error } = await supabase
      .from("skill_records")
      .select("id, user_id, procedure_id, role, label, level, evaluation_history, created_at")
      .eq("organization_id", organizationId)
      .in("level", INDEPENDENT_PLUS_LEVELS)
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    page += 1;
  }
  return all;
}

// evaluation_history を解析して「最後に 独り立ち として登録された日」を返す。見つからない場合は null。
function extractLastIndependentDate(evaluationHistory) {
  if (!Array.isArray(evaluationHistory) || evaluationHistory.length === 0) return null;
  let latest = null;
  for (const entry of evaluationHistory) {
    if (!entry || entry.level !== INDEPENDENT_LEVEL) continue;
    const d = entry.evaluated_at || entry.date || null;
    if (!d) continue;
    if (!latest || String(d).localeCompare(latest) > 0) latest = String(d);
  }
  return latest;
}

// 自立日の決定: history の 最後の 独り立ち エントリ or created_at の、新しい方を採用。
// 同じ (user, procedure, role, label) に対する重複カウントは呼び出し側で排除する。
function determineIndependentDate(record) {
  const histDate = extractLastIndependentDate(record.evaluation_history);
  const createdDate = record.created_at ? String(record.created_at).slice(0, 10) : null;
  if (histDate && createdDate) {
    return histDate.localeCompare(createdDate) > 0 ? histDate : createdDate;
  }
  return histDate || createdDate || null;
}

export default function IndependenceTrendSection({
  organizationId,
  filters,
  departments,
  procedures,
  staffList,
  searchCount = 0,
}) {
  const [skillRecords, setSkillRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedProcedureId, setSelectedProcedureId] = useState(null);
  const [sortDir, setSortDir] = useState("desc");

  useEffect(() => {
    const run = async () => {
      if (!organizationId || searchCount === 0) return;
      setLoading(true);
      setError("");
      try {
        const data = await fetchIndependentCapableSkillRecords(organizationId);
        setSkillRecords(data);
      } catch (e) {
        console.error(e);
        setError(e.message || "取得失敗");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [organizationId, searchCount]);

  const deptMap = useMemo(() => Object.fromEntries(departments.map((d) => [d.id, d])), [departments]);
  const procMap = useMemo(() => Object.fromEntries(procedures.map((p) => [p.id, p])), [procedures]);
  const staffMap = useMemo(() => Object.fromEntries(staffList.map((s) => [s.id, s])), [staffList]);

  // 「自立イベント」のリストに展開
  // 同じ (user, procedure, role, label) については **最新の独り立ち登録日** だけを採用
  // (DB 重複行、history 内の 独り立ち 複数エントリ、どちらにも対応)
  const events = useMemo(() => {
    const grouped = new Map();
    skillRecords.forEach((r) => {
      const d = determineIndependentDate(r);
      if (!d) return;
      const key = `${r.user_id}|${r.procedure_id}|${r.role}|${r.label || ""}`;
      const existing = grouped.get(key);
      if (!existing || d.localeCompare(existing.date) > 0) {
        grouped.set(key, {
          id: r.id,
          user_id: r.user_id,
          procedure_id: r.procedure_id,
          role: r.role,
          label: r.label || null,
          date: d,
        });
      }
    });
    return [...grouped.values()];
  }, [skillRecords]);

  // 期間 + フィルタ適用
  // procedure_id が procMap に無いレコードも捨てずに通す (未登録術式として扱う)。
  // ただし 診療科/術式/レベル/必須区分 のフィルタが指定されている場合は、
  // proc が解決できないレコードは除外する。
  const filteredEvents = useMemo(() => {
    const fromDate = filters.dateFrom ? `${filters.dateFrom}-01` : null;
    let toDate = null;
    if (filters.dateTo) {
      const [ty, tm] = filters.dateTo.split("-").map(Number);
      const lastDay = new Date(ty, tm, 0).getDate();
      toDate = `${filters.dateTo}-${String(lastDay).padStart(2, "0")}`;
    }
    return events.filter((e) => {
      if (fromDate && e.date < fromDate) return false;
      if (toDate && e.date > toDate) return false;

      const proc = procMap[e.procedure_id];
      const hasProc = !!proc;

      if (filters.deptIds.size > 0) {
        if (!hasProc || !filters.deptIds.has(proc.department_id)) return false;
      }
      if (filters.procedureIds.size > 0) {
        if (!hasProc || !filters.procedureIds.has(proc.id)) return false;
      }
      if (filters.levelIds.size > 0) {
        if (!hasProc) return false;
        const roleEntry = (proc.required_roles || []).find((rr) => rr.type === e.role);
        const effectiveLevelId = (roleEntry && roleEntry.levelId) || proc.level_id || null;
        // レベル未設定 (null) は常に通す。 ユーザーがレベルで絞っていてもノイズにならないよう残す。
        if (effectiveLevelId && !filters.levelIds.has(effectiveLevelId)) return false;
      }
      if (filters.requirements.size > 0 && hasProc) {
        const roleEntry = (proc.required_roles || []).find((rr) => rr.type === e.role);
        const requirement = (roleEntry && roleEntry.requirement) || "required";
        if (!filters.requirements.has(requirement)) return false;
      }

      // 役割フィルタ
      if (filters.roles && filters.roles.size > 0 && !filters.roles.has(e.role)) return false;

      // スタッフ (unknown はドロップ: スキル評価対象外の職種など)
      const staff = staffMap[e.user_id];
      if (!staff) return false;
      if (filters.years.size > 0 && !filters.years.has(String(staff.year))) return false;
      if (filters.staffIds.size > 0 && !filters.staffIds.has(staff.id)) return false;

      return true;
    });
  }, [events, procMap, staffMap, filters]);

  const months = useMemo(
    () => listMonths(filters.dateFrom, filters.dateTo),
    [filters.dateFrom, filters.dateTo]
  );

  // 術式ごとに (月別 new + 累計) の時系列を作る。 役割ごとに stack する。
  const perProcedureSeries = useMemo(() => {
    const byProc = new Map();
    filteredEvents.forEach((e) => {
      if (!byProc.has(e.procedure_id)) byProc.set(e.procedure_id, []);
      byProc.get(e.procedure_id).push(e);
    });
    const list = [];
    byProc.forEach((evts, pid) => {
      // monthly 新規カウント (役割別)
      const monthly = Object.fromEntries(months.map((m) => [m, { scrub: 0, circulating: 0 }]));
      evts.forEach((e) => {
        const m = e.date.slice(0, 7);
        if (!monthly[m]) return;
        if (e.role === "scrub") monthly[m].scrub += 1;
        else if (e.role === "circulating") monthly[m].circulating += 1;
      });
      // 累計
      let runScrub = 0, runCirc = 0;
      const data = months.map((m) => {
        runScrub += monthly[m].scrub;
        runCirc += monthly[m].circulating;
        return { month: m, scrub: runScrub, circulating: runCirc };
      });
      const name = procMap[pid]?.name || "(未登録術式)";
      const deptName = procMap[pid] ? (deptMap[procMap[pid].department_id]?.name || "") : "";
      const scrubTotal = evts.filter((e) => e.role === "scrub").length;
      const circTotal = evts.filter((e) => e.role === "circulating").length;
      list.push({
        procedureId: pid,
        procedureName: name,
        departmentName: deptName,
        data,
        total: evts.length,
        scrubTotal,
        circTotal,
      });
    });
    list.sort((a, b) => b.total - a.total || a.procedureName.localeCompare(b.procedureName, "ja"));
    return list;
  }, [filteredEvents, months, procMap, deptMap]);

  const seriesProcedures = useMemo(
    () => perProcedureSeries.map((s) => s.procedureId),
    [perProcedureSeries]
  );

  useEffect(() => {
    if (selectedProcedureId && !seriesProcedures.includes(selectedProcedureId)) {
      if (filters.procedureIds.size > 0 && !filters.procedureIds.has(selectedProcedureId)) {
        setSelectedProcedureId(null);
      }
    }
  }, [seriesProcedures, selectedProcedureId, filters.procedureIds]);

  const tableRecords = useMemo(() => {
    const scope = selectedProcedureId
      ? filteredEvents.filter((e) => e.procedure_id === selectedProcedureId)
      : filteredEvents;
    const rows = scope.map((e) => {
      const staff = staffMap[e.user_id];
      const proc = procMap[e.procedure_id];
      return {
        id: e.id,
        name: staff ? `${staff.last_name || ""} ${staff.first_name || ""}`.trim() : "(不明)",
        employee_id: staff?.employee_id || "",
        procedure: proc?.name || "(不明)",
        role: e.role,
        label: e.label,
        recognized_date: e.date,
      };
    });
    rows.sort((a, b) => {
      const cmp = (a.recognized_date || "").localeCompare(b.recognized_date || "");
      return sortDir === "asc" ? cmp : -cmp;
    });
    return rows;
  }, [filteredEvents, selectedProcedureId, staffMap, procMap, sortDir]);

  const handleBarClick = (procedureId) => {
    setSelectedProcedureId((prev) => (prev === procedureId ? null : procedureId));
  };

  const totalCount = filteredEvents.length;
  const monthlyAvg = months.length > 0 ? (totalCount / months.length).toFixed(1) : "0";

  if (searchCount === 0) {
    return (
      <div style={styles.placeholder}>
        <Search size={16} style={{ marginRight: 6, verticalAlign: "middle" }} />
        上部で条件を設定し、「検索を実行」を押すと集計が表示されます。
      </div>
    );
  }

  if (loading) {
    return (
      <div style={styles.loadingBig}>
        <div style={styles.spinner} />
        <div style={{ fontSize: 13, color: "#1d4ed8", fontWeight: 600 }}>データを読み込み中...</div>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.kpiRow}>
        <KpiCard label="期間内の自立者総数" value={totalCount} />
        <KpiCard label="月平均" value={monthlyAvg} />
        <KpiCard label="対象術式数" value={seriesProcedures.length} />
        <KpiCard label="対象月数" value={months.length} />
      </div>

      <div style={styles.note}>
        自立 = <strong>skill_records.level</strong> が「独り立ち」に到達した日。
        <code>evaluation_history</code> に「独り立ち」エントリがあればその日付、
        無ければ <code>created_at</code> を使用 (両方あれば新しい方)。 下のグラフは累積棒グラフ (月を追うごとに増える)。
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}
      {loading && <div style={styles.loadingBox}>データを読み込み中...</div>}

      <div style={styles.chartCard}>
        <div style={styles.chartHeader}>
          <h3 style={styles.chartTitle}>術式別 累積自立者推移</h3>
          <span style={{ fontSize: 11, color: "#64748b" }}>
            {perProcedureSeries.length} 術式 (スクロールで全て表示)
          </span>
        </div>
        {perProcedureSeries.length === 0 ? (
          <div style={styles.empty}>該当する自立記録がありません</div>
        ) : (
          <div style={styles.chartList}>
            {perProcedureSeries.map((s) => {
              const isSelected = selectedProcedureId === s.procedureId;
              return (
                <div
                  key={s.procedureId}
                  onClick={() => handleBarClick(s.procedureId)}
                  style={{
                    ...styles.miniChart,
                    borderColor: isSelected ? "#1d4ed8" : "#e5e7eb",
                    background: isSelected ? "#eff6ff" : "#fff",
                  }}
                >
                  <div style={styles.miniHeader}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={styles.miniDept}>{s.departmentName || "—"}</div>
                      <div style={styles.miniName}>{s.procedureName}</div>
                    </div>
                    <div style={styles.miniTotal}>累計 {s.total}</div>
                  </div>
                  <div style={styles.miniRoleBadges}>
                    <span style={{ ...styles.roleBadge, background: ROLE_COLOR.scrub }}>
                      {ROLE_LABEL.scrub} {s.scrubTotal}
                    </span>
                    <span style={{ ...styles.roleBadge, background: ROLE_COLOR.circulating }}>
                      {ROLE_LABEL.circulating} {s.circTotal}
                    </span>
                  </div>
                  <ResponsiveContainer width="100%" height={140}>
                    <BarChart data={s.data} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={40} />
                      <Tooltip
                        contentStyle={{ fontSize: 11 }}
                        formatter={(v, k) => {
                          if (k === "scrub") return [v, ROLE_LABEL.scrub];
                          if (k === "circulating") return [v, ROLE_LABEL.circulating];
                          return [v, k];
                        }}
                        labelFormatter={(label) => label}
                      />
                      <Bar dataKey="scrub" stackId="role" fill={ROLE_COLOR.scrub} />
                      <Bar dataKey="circulating" stackId="role" fill={ROLE_COLOR.circulating} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={styles.tableCard}>
        <div style={styles.tableHeader}>
          <h3 style={styles.chartTitle}>
            自立者一覧
            {selectedProcedureId && (
              <span style={{ fontSize: 12, color: "#1d4ed8", marginLeft: 10 }}>
                — {procMap[selectedProcedureId]?.name}
              </span>
            )}
          </h3>
          <span style={{ fontSize: 11, color: "#64748b" }}>{tableRecords.length} 件</span>
        </div>
        {tableRecords.length === 0 ? (
          <div style={styles.empty}>該当する自立者がいません</div>
        ) : (
          <div style={{ overflow: "auto", maxHeight: 420 }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>名前</th>
                  <th style={styles.th}>社員番号</th>
                  <th style={styles.th}>術式</th>
                  <th style={styles.th}>役割</th>
                  <th
                    style={{ ...styles.th, cursor: "pointer", userSelect: "none" }}
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                  >
                    自立日 {sortDir === "asc" ? "▲" : "▼"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {tableRecords.map((r) => (
                  <tr key={r.id}>
                    <td style={styles.td}>{r.name}</td>
                    <td style={styles.td}>{r.employee_id}</td>
                    <td style={styles.td}>
                      {r.procedure}{r.label ? ` (${r.label})` : ""}
                    </td>
                    <td style={styles.td}>{ROLE_LABEL[r.role] || r.role}</td>
                    <td style={styles.td}>{r.recognized_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value }) {
  return (
    <div style={styles.kpiCard}>
      <div style={styles.kpiLabel}>{label}</div>
      <div style={styles.kpiValue}>{value}</div>
    </div>
  );
}

const styles = {
  placeholder: {
    padding: 50, textAlign: "center", color: "#64748b", fontSize: 13,
    background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1",
  },
  loadingBig: {
    padding: 40, display: "flex", flexDirection: "column", alignItems: "center",
    gap: 12, background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10,
  },
  spinner: {
    width: 32, height: 32, borderRadius: "50%",
    border: "3px solid #bfdbfe", borderTopColor: "#1d4ed8",
    animation: "spin 0.8s linear infinite",
  },
  chartList: {
    maxHeight: 600, overflowY: "auto",
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
    gap: 10, padding: 4,
  },
  miniChart: {
    border: "1.5px solid #e5e7eb", borderRadius: 10, padding: 10,
    cursor: "pointer", transition: "all 0.15s", background: "#fff",
  },
  miniHeader: {
    display: "flex", alignItems: "center", gap: 8, marginBottom: 4,
  },
  miniDept: { fontSize: 10, color: "#64748b", fontWeight: 600 },
  miniName: { fontSize: 13, fontWeight: 700, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  miniTotal: { fontSize: 11, fontWeight: 700, color: "#1d4ed8", background: "#eff6ff", padding: "2px 8px", borderRadius: 10, flexShrink: 0 },
  miniRoleBadges: { display: "flex", gap: 4, marginBottom: 4 },
  roleBadge: {
    fontSize: 10, fontWeight: 700, color: "#fff",
    padding: "1px 7px", borderRadius: 8,
  },
  kpiRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 10 },
  kpiCard: { background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 14px" },
  kpiLabel: { fontSize: 11, color: "#64748b", fontWeight: 600 },
  kpiValue: { fontSize: 22, fontWeight: 700, color: "#0f172a", marginTop: 2 },
  note: {
    background: "#f0f9ff", border: "1px solid #bae6fd", color: "#075985",
    padding: "6px 10px", borderRadius: 8, fontSize: 11, marginBottom: 12,
  },
  errorBox: {
    background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10,
  },
  loadingBox: {
    background: "#eff6ff", border: "1px solid #bfdbfe", color: "#1d4ed8",
    padding: "8px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10,
  },
  chartCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 },
  chartHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  chartTitle: { fontSize: 14, fontWeight: 700, color: "#0f172a", margin: 0 },
  resetSelBtn: {
    fontSize: 11, padding: "4px 10px",
    background: "#eff6ff", color: "#1d4ed8",
    border: "1px solid #bfdbfe", borderRadius: 6, cursor: "pointer",
  },
  tableCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16 },
  tableHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: {
    padding: "8px 10px", textAlign: "left",
    borderBottom: "2px solid #e2e8f0",
    fontSize: 11, color: "#475569", fontWeight: 700,
    textTransform: "uppercase", letterSpacing: "0.5px",
    background: "#f8fafc",
  },
  td: { padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#0f172a" },
  empty: {
    padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 12,
    background: "#f8fafc", borderRadius: 8, border: "1px dashed #cbd5e1",
  },
  tooltip: {
    background: "#fff", border: "1px solid #cbd5e1", borderRadius: 8,
    padding: 10, boxShadow: "0 4px 12px rgba(0,0,0,0.08)", maxWidth: 280,
  },
};
